import { useState } from 'react';
import type { FormEvent } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Field } from '../../components/ui/Field';
import { useFormErrors } from '../../lib/useFormErrors';
import { employeeMutations } from '../../lib/queries';
import { EMPLOYMENT_LABELS } from '../../lib/labels';
import { useToast } from '../../components/ui/Toast';
import type { Crew, Employee, EmploymentType } from '../../lib/types';

type FormState = Omit<Employee, 'id' | 'displayName'>;

const BLANK: FormState = {
  firstName: '',
  lastName: '',
  shortName: '',
  crewId: null,
  role: '',
  phone: '',
  employmentType: 'vollzeit',
  weeklyHours: 40,
  vacationEntitlement: 30,
  overtimeStartBalance: 0,
  entryDate: null,
  notes: '',
  active: true,
  sortOrder: 0,
};

interface Props {
  employee: Employee | null;
  crews: Crew[];
  onClose: () => void;
}

export function EmployeeDialog({ employee, crews, onClose }: Props) {
  const isEdit = employee !== null;
  const [form, setForm] = useState<FormState>(employee ? toFormState(employee) : BLANK);
  const { errors, capture, clear, fieldError } = useFormErrors();
  const toast = useToast();

  const create = employeeMutations.useCreate();
  const update = employeeMutations.useUpdate();
  const busy = create.isPending || update.isPending;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    clear();

    const onSuccess = () => {
      toast.success(
        isEdit ? 'Mitarbeiter gespeichert.' : `${form.firstName} ${form.lastName} wurde angelegt.`,
      );
      onClose();
    };

    if (isEdit) {
      update.mutate({ ...form, id: employee.id }, { onSuccess, onError: capture });
    } else {
      create.mutate(form, { onSuccess, onError: capture });
    }
  };

  return (
    <Modal
      title={isEdit ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}
      subtitle={isEdit ? employee.displayName : 'Stammdaten für die Planung'}
      onClose={onClose}
      width="voll"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button type="submit" form="employee-form" className="btn btn-signal" disabled={busy}>
            {busy ? 'Wird gespeichert …' : 'Speichern'}
          </button>
        </>
      }
    >
      <form id="employee-form" className="stack" onSubmit={submit} noValidate>
        {errors.general ? (
          <p className="form-error" role="alert">
            {errors.general}
          </p>
        ) : null}

        <div className="form-grid">
          <span className="label form-section">Person</span>

          <Field label="Vorname" error={fieldError('firstName')}>
            {(props) => (
              <input
                {...props}
                className="input"
                autoFocus
                value={form.firstName}
                onChange={(event) => set('firstName', event.target.value)}
              />
            )}
          </Field>

          <Field label="Nachname" error={fieldError('lastName')}>
            {(props) => (
              <input
                {...props}
                className="input"
                value={form.lastName}
                onChange={(event) => set('lastName', event.target.value)}
              />
            )}
          </Field>

          <Field
            label="Kürzel"
            error={fieldError('shortName')}
            hint="Für enge Spalten im Plan. Leer lassen = Initialen."
          >
            {(props) => (
              <input
                {...props}
                className="input"
                maxLength={12}
                placeholder={initialsOf(form)}
                value={form.shortName}
                onChange={(event) => set('shortName', event.target.value)}
              />
            )}
          </Field>

          <span className="label form-section">Einsatz</span>

          <Field label="Kolonne" error={fieldError('crewId')}>
            {(props) => (
              <select
                {...props}
                className="select"
                value={form.crewId ?? ''}
                onChange={(event) => set('crewId', event.target.value ? Number(event.target.value) : null)}
              >
                <option value="">Ohne Kolonne</option>
                {crews.map((crew) => (
                  <option key={crew.id} value={crew.id}>
                    {crew.name}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Funktion" error={fieldError('role')} hint="z. B. Polier, Maurer, Baggerfahrer">
            {(props) => (
              <input
                {...props}
                className="input"
                value={form.role}
                onChange={(event) => set('role', event.target.value)}
              />
            )}
          </Field>

          <Field label="Beschäftigung" error={fieldError('employmentType')}>
            {(props) => (
              <select
                {...props}
                className="select"
                value={form.employmentType}
                onChange={(event) => set('employmentType', event.target.value as EmploymentType)}
              >
                {Object.entries(EMPLOYMENT_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <span className="label form-section">Konten und Kontakt</span>

          <Field label="Telefon" error={fieldError('phone')}>
            {(props) => (
              <input
                {...props}
                className="input"
                type="tel"
                value={form.phone}
                onChange={(event) => set('phone', event.target.value)}
              />
            )}
          </Field>

          <Field label="Eintrittsdatum" error={fieldError('entryDate')}>
            {(props) => (
              <input
                {...props}
                className="input"
                type="date"
                value={form.entryDate ?? ''}
                onChange={(event) => set('entryDate', event.target.value || null)}
              />
            )}
          </Field>

          <Field label="Wochenstunden" error={fieldError('weeklyHours')}>
            {(props) => (
              <input
                {...props}
                className="input"
                type="number"
                min={0}
                max={80}
                step={0.5}
                value={form.weeklyHours}
                onChange={(event) => set('weeklyHours', Number(event.target.value))}
              />
            )}
          </Field>

          <Field
            label="Urlaubsanspruch"
            error={fieldError('vacationEntitlement')}
            hint="Tage pro Jahr"
          >
            {(props) => (
              <input
                {...props}
                className="input"
                type="number"
                min={0}
                max={90}
                step={0.5}
                value={form.vacationEntitlement}
                onChange={(event) => set('vacationEntitlement', Number(event.target.value))}
              />
            )}
          </Field>

          <Field
            label="Überstunden-Startsaldo"
            error={fieldError('overtimeStartBalance')}
            hint="Übertrag beim Einrichten, in Stunden"
          >
            {(props) => (
              <input
                {...props}
                className="input"
                type="number"
                step={0.25}
                value={form.overtimeStartBalance}
                onChange={(event) => set('overtimeStartBalance', Number(event.target.value))}
              />
            )}
          </Field>

          <Field label="Reihenfolge im Plan" error={fieldError('sortOrder')} hint="Kleinere Zahl zuerst">
            {(props) => (
              <input
                {...props}
                className="input"
                type="number"
                min={0}
                value={form.sortOrder}
                onChange={(event) => set('sortOrder', Number(event.target.value))}
              />
            )}
          </Field>

          <Field label="Notiz" error={fieldError('notes')} className="span-2">
            {(props) => (
              <textarea
                {...props}
                className="textarea"
                value={form.notes}
                onChange={(event) => set('notes', event.target.value)}
              />
            )}
          </Field>
        </div>

        <label className="check">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(event) => set('active', event.target.checked)}
          />
          Aktiv — erscheint im Schichtplan
        </label>
      </form>
    </Modal>
  );
}

function toFormState(employee: Employee): FormState {
  const { id: _id, displayName: _displayName, ...rest } = employee;
  return rest;
}

function initialsOf(form: FormState): string {
  return `${form.firstName.slice(0, 1)}${form.lastName.slice(0, 1)}`.toUpperCase() || 'z. B. JH';
}
